import { Tooltip as TooltipPrimitive } from 'radix-ui'
import type * as React from 'react'

import { useDocumentDirection } from '../../hooks/use-document-direction'
import { cn } from '../../lib/utils'

/**
 * 120ms delay by default (2g). Long enough not to fire on a mouse crossing the
 * grid, short enough to feel immediate on a deliberate hover.
 */
function TooltipProvider({
  delayDuration = 120,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

function Tooltip(props: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />
}

function TooltipTrigger(props: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />
}

/**
 * Solid ink, no arrow tail (2g).
 *
 * There is deliberately no TooltipArrow export. A tail on a square, hard-edged
 * tooltip reads as a speech bubble, which is the wrong register for a product
 * that shows ledger balances.
 */
function TooltipContent({
  className,
  sideOffset = 6,
  dir: providedDir,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  const documentDirection = useDocumentDirection()
  const dir = providedDir ?? documentDirection

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'z-50 w-fit max-w-64 rounded-sm bg-foreground px-2 py-1 text-small text-background',
          'duration-[var(--motion-base)] data-[state=delayed-open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0',
          className,
        )}
        {...props}
        dir={dir}
      />
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger }
